import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAppLockStore } from "@/store/appLockStore";
import { appLockUserKey } from "@/utils/appLock";
import AppLockOverlay from "@/components/app-lock/AppLockOverlay";

// Survives StrictMode's extra effect run, so a restored session is not treated as a fresh login.
let initialUserKey;

export default function AppLockGate() {
  const { user, loading } = useAuth();
  const userKey = appLockUserKey(user);
  const locked = useAppLockStore((state) => state.locked);
  const enabled = useAppLockStore((state) => {
    const record = state.activeUserKey ? state.records[state.activeUserKey] : null;
    return !!record?.enabled;
  });
  const bindRestoredSession = useAppLockStore((state) => state.bindRestoredSession);
  const bindFreshSession = useAppLockStore((state) => state.bindFreshSession);
  const releaseLockSession = useAppLockStore((state) => state.releaseLockSession);
  const touch = useAppLockStore((state) => state.touch);
  const markHidden = useAppLockStore((state) => state.markHidden);
  const lockIfIdle = useAppLockStore((state) => state.lockIfIdle);

  useEffect(() => {
    if (loading) return;
    if (initialUserKey === undefined) {
      initialUserKey = userKey;
      if (userKey) bindRestoredSession(userKey);
      return;
    }
    if (userKey === initialUserKey) return;
    initialUserKey = userKey;
    if (!userKey) releaseLockSession();
    else bindFreshSession(userKey);
  }, [loading, userKey, bindRestoredSession, bindFreshSession, releaseLockSession]);

  useEffect(() => {
    if (!enabled || locked) return undefined;

    const onActivity = () => touch();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") markHidden();
      else lockIfIdle();
    };

    lockIfIdle();
    const id = window.setInterval(lockIfIdle, 1000);
    window.addEventListener("pointerdown", onActivity, true);
    window.addEventListener("keydown", onActivity, true);
    window.addEventListener("scroll", onActivity, true);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", lockIfIdle);
    window.addEventListener("focus", lockIfIdle);
    window.addEventListener("pagehide", markHidden);

    return () => {
      window.clearInterval(id);
      window.removeEventListener("pointerdown", onActivity, true);
      window.removeEventListener("keydown", onActivity, true);
      window.removeEventListener("scroll", onActivity, true);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", lockIfIdle);
      window.removeEventListener("focus", lockIfIdle);
      window.removeEventListener("pagehide", markHidden);
    };
  }, [enabled, locked, touch, markHidden, lockIfIdle]);

  if (!locked) return null;
  return <AppLockOverlay />;
}
